'use client';

import { Text } from '@/components/ui/text';
import { Button } from '@/components/ui/button/Button';
import { ButtonSize, ButtonVariant } from '@/components/ui/button/Button.types';
import { Colors } from '@/lib/colorUtils';
import { cn } from '@/lib/styleUtils';
import { EmptyStateActionVariant } from './EmptyState.types';
import type { EmptyStateProps } from './EmptyState.types';

const ROOT_FONT_PX = 16;

const variantMap: Record<EmptyStateActionVariant, ButtonVariant> = {
  [EmptyStateActionVariant.primary]: ButtonVariant.primary,
  [EmptyStateActionVariant.secondary]: ButtonVariant.secondary,
};

const ICON_CONTAINER_DEFAULT_SIZE = 80;

export const EmptyState = ({
  illustration,
  illustrationWidth,
  illustrationHeight,
  illustrationAlt = '',
  iconContainer,
  title,
  subtitle,
  actions,
  centered = false,
  className,
  'data-cy': dataCy = 'empty-state',
}: EmptyStateProps) => {
  const renderIllustration = () => {
    if (illustration === undefined) {
      if (!iconContainer) return null;
      const size = iconContainer.size ?? ICON_CONTAINER_DEFAULT_SIZE;
      return (
        <div
          className="tw:flex tw:items-center tw:justify-center tw:rounded-full tw:bg-gray-lighter tw:text-gray-darker tw:shrink-0"
          // eslint-disable-next-line react/forbid-dom-props -- dynamic runtime value (icon container size from prop)
          style={{ width: `${size / ROOT_FONT_PX}rem`, height: `${size / ROOT_FONT_PX}rem` }}
          aria-hidden="true"
          data-cy="empty-state-icon-container"
        >
          {iconContainer.icon}
        </div>
      );
    }
    if (typeof illustration !== 'string') return illustration;

    const style: { width?: string; height?: string } = {};
    if (illustrationWidth !== undefined) {
      style.width = `${illustrationWidth / ROOT_FONT_PX}rem`;
      style.height =
        illustrationHeight !== undefined
          ? `${illustrationHeight / ROOT_FONT_PX}rem`
          : 'auto';
    }

    return (
      <img
        src={illustration}
        alt={illustrationAlt}
        aria-hidden={illustrationAlt === '' ? 'true' : undefined}
        width={illustrationWidth}
        height={illustrationHeight}
        // eslint-disable-next-line react/forbid-dom-props -- dynamic runtime value (illustration size computed from props)
        style={style}
        data-cy="empty-state-illustration"
      />
    );
  };

  const card = (
    <div
      className={cn(
        'tw:flex tw:flex-col tw:items-center tw:gap-huge tw:bg-white tw:rounded-8 tw:px-base tw:py-huge tw:w-full',
        className
      )}
      data-cy={dataCy}
    >
      {renderIllustration()}
      <div className="tw:flex tw:flex-col tw:items-center tw:gap-huge">
        {/* Title -> subtitle gap is the 12px `small` token, per the design. It was `micro` (8px),
            which is a step off the spec everywhere the template is used. */}
        <div className="tw:flex tw:flex-col tw:items-center tw:gap-small">
          <Text
            size="20"
            weight="bold"
            color={Colors.colorFont}
            className="tw:text-center"
            data-cy="empty-state-title"
          >
            {title}
          </Text>
          {subtitle && (
            <Text
              size="16"
              color={Colors.colorGrayDarker}
              className="tw:text-center"
              data-cy="empty-state-subtitle"
            >
              {subtitle}
            </Text>
          )}
        </div>
        {actions && actions.length > 0 && (
          <div className="tw:flex tw:flex-row tw:justify-center tw:gap-base">
            {actions.map((action) => (
              <Button
                key={action.label}
                size={ButtonSize.large}
                variant={variantMap[action.variant ?? EmptyStateActionVariant.primary]}
                onClick={action.onClick}
                data-cy={action['data-cy']}
              >
                {action.label}
              </Button>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  // `centered` wraps the card to fill height and center it (replaces the per-page wrapper).
  if (!centered) return card;

  return (
    <div className="tw:flex tw:flex-1 tw:flex-col tw:items-center tw:justify-center tw:w-full">
      {card}
    </div>
  );
};
